import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { resumePendingDownloadAfterAuth } from '../../../utils/downloadResume';
import { apiUrl } from '../../../utils/apiBase';
import PasswordVisibilityIcon from './PasswordVisibilityIcon';
import { getConfig } from '../../../config/appConfig';
import { defaultLogoCdnUrl, legacyApiPath } from '../../../config/environments';
import { CloudAuthButtons } from '../../auth/cloudAuth';

export default function LoginModal({ isOpen, onClose }) {
  const { login } = useAuth();
  const navigate = useNavigate();

  // modes: 'login' | 'register' | 'verify' | 'forgot' | 'reset-password'
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);

  // Password-reset specific state
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [forgotAwaitingCode, setForgotAwaitingCode] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMode('login');
      setError('');
      setPassword('');
      setOtp('');
      setResetOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setCaptchaVerified(false);
      setForgotAwaitingCode(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mode === 'register' && window.grecaptcha) {
      const timer = setTimeout(() => {
        try {
          window.grecaptcha.render('login-recaptcha-container', {
            sitekey: getConfig('recaptcha.siteKey'),
            callback: () => setCaptchaVerified(true),
            'expired-callback': () => setCaptchaVerified(false),
          });
        } catch (e) {
          // Captcha container may already be rendered
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  function switchMode(next) {
    setMode(next);
    setError('');
    if (next !== 'forgot') {
      setForgotAwaitingCode(false);
    }
  }

  const handleFinishAuth = async () => {
    const downloaded = await resumePendingDownloadAfterAuth();
    if (downloaded) {
      toast.success('Your Candor IDE download has started.');
    } else {
      toast.success('Successfully logged in.');
    }
    onClose();
    navigate('/dashboard');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      await handleFinishAuth();
    } catch (err) {
      setError(err.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!captchaVerified && process.env.NODE_ENV !== 'development') {
      toast.error('Please verify the reCAPTCHA first.');
      return;
    }
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedCompany = company.trim();
    if (!trimmedEmail || !password || !trimmedFirstName || !trimmedLastName) {
      setError('Email, password, first name, and last name are required.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_REGISTER') || legacyApiPath('register')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          password,
          first_name: trimmedFirstName,
          last_name: trimmedLastName,
          company: trimmedCompany,
          plan_name: 'Community Edition',
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          typeof data.detail === 'string'
            ? data.detail
            : 'Registration failed. Please try again.'
        );
      }
      toast.success('Verification code sent to your email.');
      setMode('verify');
      setOtp('');
    } catch (err) {
      setError(err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_VERIFY_OTP') || legacyApiPath('verify_otp')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp: otp.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          typeof data.detail === 'string'
            ? data.detail
            : 'Invalid verification code.'
        );
      }
      toast.success('Email verified. Signing you in…');
      await login(email.trim(), password);
      await handleFinishAuth();
    } catch (err) {
      setError(err.message || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const res = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_RESEND_OTP') || legacyApiPath('resend_otp')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) throw new Error('Resend failed.');
      toast.success('Verification code resent.');
    } catch (err) {
      toast.error('Failed to resend code. Please try again.');
    }
  };

  // ── Step 1: Send OTP to email ─────────────────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_FORGOT_PASSWORD') || legacyApiPath('forgot_password')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.detail === 'string'
            ? data.detail
            : 'Could not send reset code. Please try again.'
        );
      }
      // Soft-match: do not imply account exists; let user open OTP step when ready
      toast.success(
        data.message ||
          'If an account exists for this email, a verification code has been sent.'
      );
      setResetOtp('');
      setNewPassword('');
      setConfirmPassword('');
      // Stay on forgot form; user advances only when they choose to enter a code
      setForgotAwaitingCode(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP + set new password (single call to /reset-password) ─
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    // Mirror backend validation rules client-side for instant feedback
    if (newPassword.length < 12 || newPassword.length > 34) {
      setError('Password must be 12–34 characters long.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter (A-Z).');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter (a-z).');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain at least one number (0-9).');
      return;
    }
    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setError('Password must contain at least one special character (@, #, $, %, &, !, etc.).');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_RESET_PASSWORD') || legacyApiPath('reset_password')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp: resetOtp.trim(),
          newPassword: newPassword,   // backend expects camelCase "newPassword"
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.detail === 'string'
            ? data.detail
            : 'Failed to reset password. Please try again.'
        );
      }
      toast.success('Password reset successfully! Please sign in.');
      setResetOtp('');
      setNewPassword('');
      setConfirmPassword('');
      switchMode('login');
    } catch (err) {
      setError(err.message || 'Password reset failed.');
    } finally {
      setLoading(false);
    }
  };

  const title = {
    login: 'Welcome Back',
    register: 'Create Your Account',
    verify: 'Verify Your Email',
    forgot: 'Reset Your Password',
    'reset-password': 'Set New Password',
  }[mode];

  const subtitle = {
    login: 'Sign in to your Candor Dashboard',
    register: 'Start your free Candor account.',
    verify: 'Enter the 6-digit code sent to your email.',
    forgot: "Enter your email and we'll send a reset code.",
    'reset-password': 'Enter the reset code from your email, then choose a new password.',
  }[mode];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-100 p-8 sm:p-10 animate-fade-in max-h-[95vh] overflow-y-auto">

        {/* Background Blobs */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-teal-500/5 rounded-full blur-2xl pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 text-2xl font-semibold focus:outline-none transition-colors"
          aria-label="Close modal"
        >
          &times;
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <img
            src={`${process.env.PUBLIC_URL || ''}/candor-brand-lockup.png`}
            alt="Candor Logo"
            className="h-11 mx-auto mb-4 object-contain"
            style={{ maxWidth: '180px' }}
            onError={(e) => {
              e.target.src = `${process.env.PUBLIC_URL || ''}/candorlogo.png`;
              e.target.onerror = () => { e.target.src = defaultLogoCdnUrl(); };
            }}
          />
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">{title}</h2>
          <p className="text-slate-500 text-sm mt-1">{subtitle}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <span className="text-base">⚠</span>
            <span>{error}</span>
          </div>
        )}

        {/* ── Login form ── */}
        {mode === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <CloudAuthButtons mode="login" density="compact" disabled={loading} layout="first" />
            <div>
              <label htmlFor="modal-login-email" className="block text-xs font-semibold text-slate-700 mb-1.5">
                Work Email
              </label>
              <input
                id="modal-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
              />
            </div>
            <div>
              <label htmlFor="modal-login-pass" className="block text-xs font-semibold text-slate-700 mb-1.5">
                Password
              </label>
              <div className="relative flex items-center">
                <input
                  id="modal-login-pass"
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-3.5 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((prev) => !prev)}
                  className="absolute right-3 text-slate-400 hover:text-slate-700 focus:outline-none"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <PasswordVisibilityIcon visible={showPwd} />
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 shadow-md shadow-slate-900/10 transition-all hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Sign In with email'
              )}
            </button>
          </form>
        )}

        {/* ── Register form ── */}
        {mode === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4">
            <CloudAuthButtons mode="register" density="compact" disabled={loading} layout="first" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="modal-reg-first" className="block text-xs font-semibold text-slate-700 mb-1.5">First Name</label>
                <input id="modal-reg-first" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" required className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
              </div>
              <div>
                <label htmlFor="modal-reg-last" className="block text-xs font-semibold text-slate-700 mb-1.5">Last Name</label>
                <input id="modal-reg-last" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" required className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
              </div>
            </div>
            <div>
              <label htmlFor="modal-reg-email" className="block text-xs font-semibold text-slate-700 mb-1.5">Work Email</label>
              <input id="modal-reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" required className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
            </div>
            <div>
              <label htmlFor="modal-reg-company" className="block text-xs font-semibold text-slate-700 mb-1.5">Company (optional)</label>
              <input id="modal-reg-company" type="text" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Your company" className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
            </div>
            <div>
              <label htmlFor="modal-reg-pass" className="block text-xs font-semibold text-slate-700 mb-1.5">Password</label>
              <div className="relative flex items-center">
                <input id="modal-reg-pass" type={showPwd ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 12 chars, upper, lower, number, symbol" required minLength={12} className="w-full px-3.5 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
                <button
                  type="button"
                  onClick={() => setShowPwd((prev) => !prev)}
                  className="absolute right-3 text-slate-400 hover:text-slate-700 focus:outline-none"
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <PasswordVisibilityIcon visible={showPwd} />
                </button>
              </div>
            </div>
            <div className="flex justify-center py-1">
              <div id="login-recaptcha-container"></div>
            </div>
            <button type="submit" disabled={loading} className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 shadow-md shadow-slate-900/10 transition-all hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Create account with email'}
            </button>
          </form>
        )}

        {/* ── Registration OTP verify ── */}
        {mode === 'verify' && (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <p className="text-sm text-slate-500 text-center">
              Code sent to <strong>{email}</strong>
            </p>
            <div>
              <label htmlFor="modal-verify-otp" className="block text-xs font-semibold text-slate-700 mb-1.5">Verification Code</label>
              <input id="modal-verify-otp" type="text" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" maxLength={8} required className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm text-center tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
            </div>
            <button type="submit" disabled={loading} className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md shadow-blue-500/10 transition-all hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Verify & Continue →'}
            </button>
            <div className="flex justify-between text-xs pt-1">
              <button type="button" onClick={handleResendOtp} className="text-blue-600 hover:underline">Resend code</button>
              <button type="button" onClick={() => switchMode('register')} className="text-slate-500 hover:underline">Back</button>
            </div>
          </form>
        )}

        {/* ── Forgot password: enter email ── */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label htmlFor="modal-forgot-email" className="block text-xs font-semibold text-slate-700 mb-1.5">Work Email</label>
              <input
                id="modal-forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
              />
            </div>
            {forgotAwaitingCode && (
              <p className="text-xs text-slate-600 text-center">
                If an account exists for this email, a code was sent. Enter it when you receive it.
              </p>
            )}
            <button type="submit" disabled={loading} className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md shadow-blue-500/10 transition-all hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (forgotAwaitingCode ? 'Resend Code →' : 'Send Reset Code →')}
            </button>
            {forgotAwaitingCode && (
              <button
                type="button"
                className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all"
                onClick={() => switchMode('reset-password')}
              >
                I have a code →
              </button>
            )}
          </form>
        )}

        {/* ── Reset password: OTP code + new password (combined) ── */}
        {mode === 'reset-password' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-sm text-slate-500 text-center">
              Enter the code from your email for <strong>{email}</strong>
            </p>
            <div>
              <label htmlFor="modal-reset-otp" className="block text-xs font-semibold text-slate-700 mb-1.5">Reset Code</label>
              <input
                id="modal-reset-otp"
                type="text"
                value={resetOtp}
                onChange={(e) => setResetOtp(e.target.value)}
                placeholder="6-digit code from email"
                maxLength={8}
                required
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm text-center tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
              />
            </div>
            <div>
              <label htmlFor="modal-new-pwd" className="block text-xs font-semibold text-slate-700 mb-1.5">New Password</label>
              <div className="relative flex items-center">
                <input
                  id="modal-new-pwd"
                  type={showNewPwd ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 12 characters"
                  required
                  className="w-full px-3.5 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPwd((v) => !v)}
                  className="absolute right-3 text-slate-400 hover:text-slate-700 focus:outline-none"
                  aria-label={showNewPwd ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <PasswordVisibilityIcon visible={showNewPwd} />
                </button>
              </div>
              {/* Live password strength checklist */}
              {newPassword && (
                <ul className="mt-2 space-y-1">
                  {[
                    { label: '12–34 characters', ok: newPassword.length >= 12 && newPassword.length <= 34 },
                    { label: 'Uppercase letter (A-Z)', ok: /[A-Z]/.test(newPassword) },
                    { label: 'Lowercase letter (a-z)', ok: /[a-z]/.test(newPassword) },
                    { label: 'Number (0-9)', ok: /[0-9]/.test(newPassword) },
                    { label: 'Special character (@#$%&! etc.)', ok: /[^A-Za-z0-9]/.test(newPassword) },
                  ].map(({ label, ok }) => (
                    <li key={label} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-slate-400'}`}>
                      <span aria-hidden="true">{ok ? '\u2713' : '\u25CB'}</span>
                      {label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <label htmlFor="modal-confirm-pwd" className="block text-xs font-semibold text-slate-700 mb-1.5">Confirm New Password</label>
              <div className="relative flex items-center">
                <input
                  id="modal-confirm-pwd"
                  type={showConfirmPwd ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                  className="w-full px-3.5 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPwd((v) => !v)}
                  className="absolute right-3 text-slate-400 hover:text-slate-700 focus:outline-none"
                  aria-label={showConfirmPwd ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <PasswordVisibilityIcon visible={showConfirmPwd} />
                </button>
              </div>
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
              )}
              {newPassword && confirmPassword && newPassword === confirmPassword && (
                <p className="text-xs text-green-600 mt-1">{'\u2713'} Passwords match</p>
              )}
            </div>
            <button type="submit" disabled={loading} className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md shadow-blue-500/10 transition-all hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2">
              {loading ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : 'Reset Password →'}
            </button>
          </form>
        )}

        {/* Modal Footer Links */}
        <div className="mt-6 text-center text-sm text-slate-500 pt-4 border-t border-slate-100">
          {mode === 'login' && (
            <>
              <p>
                Don&apos;t have an account?{' '}
                <button type="button" className="text-blue-600 font-semibold hover:underline" onClick={() => switchMode('register')}>
                  Register free
                </button>
              </p>
              <p className="mt-2">
                <button
                  id="modal-forgot-password-btn"
                  type="button"
                  className="text-blue-600 font-semibold hover:underline"
                  onClick={() => switchMode('forgot')}
                >
                  Forgot password?
                </button>
              </p>
            </>
          )}
          {mode === 'register' && (
            <p>
              Already registered?{' '}
              <button type="button" className="text-blue-600 font-semibold hover:underline" onClick={() => switchMode('login')}>
                Sign in
              </button>
            </p>
          )}
          {mode === 'verify' && (
            <p>
              <button type="button" className="text-blue-600 font-semibold hover:underline" onClick={() => switchMode('register')}>
                Back to registration
              </button>
            </p>
          )}
          {(mode === 'forgot' || mode === 'reset-password') && (
            <p>
              <button type="button" className="text-slate-500 hover:underline" onClick={() => switchMode('login')}>
                ← Back to sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
